import React, { useEffect, useState } from "react";
import ToolsApi from "../../../../../api/ToolsApi";
import ButtonAlternateLoading from "../../../../../Icons/ButtonAlternateLoading";
import { Ri360AutoCalibrationState } from "../../../../../Model/Calibration";
import { EAutoCalibrationResult } from "../../../../../Model/EAutoCalibrationResult";
import { ECarrier } from "../../../../../Model/ECarrier";
import { EFootprint } from "../../../../../Model/PlateType";
import {
  Ri360CarrierCalibration,
  Ri360HotelCalibration,
} from "../../../../../Model/StorageConfig";
import DateUtil from "../../../../../Utility/DateUtil";
import MtiButton from "../../../../Buttons/MtiButton";
import CommonDropDown from "../../../../Common/DropDown/CommonDropDown";
import "../../../VectorEditor/WaypointEditor.css";
import "./AutoCalibrationResultView.css";

const loadingIcon = <ButtonAlternateLoading></ButtonAlternateLoading>;

export enum EMoveButtonType {
  Store,
  Retrieve,
}

export enum EMoveButtonState {
  Idle,
  Loading,
  Disabled,
}

export type MoveButtonsState = Map<EMoveButtonType, EMoveButtonState>;

type Props = {
  storageCalibrations: Ri360CarrierCalibration[];
  autoCalibrationResult: Ri360AutoCalibrationState;
  useUpdatedValue: boolean;
  selectedCarrier: ECarrier;
  selectedHotelIndex: number;
  setSelectedHotelIndex: (index: number) => void;
  selectedSlotIndex: number;
  setSelectedSlotIndex: (index: number) => void;
  moveButtonsState: MoveButtonsState;
  SetMoveButtonLoadingActive: (
    moveButtonType: EMoveButtonType,
    useUpdatedValue: boolean
  ) => void;
  style?: React.CSSProperties;
};

export default function AutoCalibrationResultView({
  storageCalibrations,
  autoCalibrationResult,
  useUpdatedValue,
  selectedCarrier,
  selectedHotelIndex,
  setSelectedHotelIndex,
  selectedSlotIndex,
  setSelectedSlotIndex,
  moveButtonsState,
  SetMoveButtonLoadingActive,
  style = {},
}: Props) {
  const [selectedStorageCalibration, setSelectedStorageCalibration] = useState(
    null as Ri360CarrierCalibration | null
  );
  const [hotels, setHotels] = useState([] as Ri360HotelCalibration[]);
  const [hotelCapacity, setHotelCapacity] = useState(1);
  const ScModuleIdentifier = selectedStorageCalibration
    ? selectedStorageCalibration.moduleIdentifier
    : "";

  useEffect(() => {
    const sc = storageCalibrations.filter(
      (s) => s.carrier == selectedCarrier
    )[0];
    setSelectedStorageCalibration(sc);
    updateHotels(sc.hotelCalibrations);
  }, [storageCalibrations, selectedCarrier]);

  useEffect(() => {
    if (selectedStorageCalibration) {
      setHotels(selectedStorageCalibration?.hotelCalibrations);
      updateHotel(
        selectedStorageCalibration?.hotelCalibrations,
        selectedHotelIndex
      );
    }
  }, [selectedStorageCalibration, selectedHotelIndex]);

  const updateHotels = (hotels: Ri360HotelCalibration[]) => {
    setHotels(hotels);
    updateHotel(hotels, 0);
  };

  const updateHotel = (hotels: Ri360HotelCalibration[], hotelIndex: number) => {
    setSelectedHotelIndex(hotelIndex);
    setHotelCapacity(hotels[hotelIndex].capacity);
    setSelectedSlotIndex(0);
    updateSlot(0);
  };

  const updateSlot = (slotIndex: number) => {
    setSelectedSlotIndex(slotIndex);
  };

  const autoCalibrationCoordinates = useUpdatedValue
    ? autoCalibrationResult.updatedPositions
    : autoCalibrationResult.currentPositions;

  // if (
  //   autoCalibrationCoordinates == null ||
  //   autoCalibrationResult.updatedPositionTime == null
  // ) {
  //   return <div>{JSON.stringify(autoCalibrationResult, null, 2)}</div>;
  // }

  if (
    autoCalibrationCoordinates == null ||
    (useUpdatedValue && autoCalibrationResult.updatedPositionTime == null)
  ) {
    return (
      <div className="auto-calibration-result-container" style={style}></div>
    );
  }

  const storageAutoCalibrationResult = [];
  for (let i = 0; i < autoCalibrationCoordinates.length; i++) {
    const autoCalibrationCoordinate = autoCalibrationCoordinates[i];
    if (autoCalibrationCoordinate.top) {
      storageAutoCalibrationResult.push(
        <tr key={`top-${autoCalibrationCoordinate.hotelNumber}`}>
          <td>Hotel {autoCalibrationCoordinate.hotelNumber} Top</td>
          <td>{autoCalibrationCoordinate.top.x.toFixed(5)}</td>
          <td>{autoCalibrationCoordinate.top.y.toFixed(5)}</td>
          <td>{autoCalibrationCoordinate.top.z.toFixed(5)}</td>
        </tr>
      );
    }
  }
  for (let i = autoCalibrationCoordinates.length - 1; i >= 0; i--) {
    const autoCalibrationCoordinate = autoCalibrationCoordinates[i];
    if (autoCalibrationCoordinate.bottom) {
      storageAutoCalibrationResult.push(
        <tr key={`botom-${autoCalibrationCoordinate.hotelNumber}`}>
          <td>Hotel {autoCalibrationCoordinate.hotelNumber} Bottom</td>
          <td>{autoCalibrationCoordinate.bottom.x.toFixed(5)}</td>
          <td>{autoCalibrationCoordinate.bottom.y.toFixed(5)}</td>
          <td>{autoCalibrationCoordinate.bottom.z.toFixed(5)}</td>
        </tr>
      );
    }
  }

  const date = DateUtil.toDate(
    (useUpdatedValue
      ? autoCalibrationResult.updatedPositionTime
      : autoCalibrationResult.currentPositionTime) ?? new Date().toISOString()
  );

  const options = {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  };

  const h = date.getHours();
  const m = date.getMinutes();

  const dateStr =
    date.toLocaleDateString("en-US", options) + ` ${h}:${m < 10 ? "0" + m : m}`;

  console.log({ dateStr });
  return (
    <div className="auto-calibration-result-container" style={style}>
      <div className="auto-calibration-result">
        <label className="auto-calibration-result-carrier">
          {useUpdatedValue
            ? "Post-Calibration Settings (Unsaved)"
            : "Current Settings"}
        </label>
        <label className="auto-calibration-result-last-updated">
          {`Last updated on ${dateStr}`}
        </label>
        <div>
          <div className="scroll-container">
            <table className="auto-calibration-result-table">
              <thead>
                <tr>
                  <th>Position Name</th>
                  <th>X</th>
                  <th>Y</th>
                  <th>Z</th>
                </tr>
              </thead>
              <tbody>{storageAutoCalibrationResult}</tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="auto-calibration-result-selector">
        <div>
          {/* hotels */}
          <div className="auto-calibration-result-select">
            <label>Hotel</label>
            <CommonDropDown<number>
              value={selectedHotelIndex}
              id="hotel-select"
              disabled={useUpdatedValue}
              hideDropIcon={useUpdatedValue}
              onChange={(value) => {
                updateHotel(hotels, value);
              }}
              style={{
                width: 100,
              }}
              dropDownStyle={{
                maxHeight: 110,
              }}
              dropDownDisplayValues={hotels.map((hotel, index) => ({
                display: EFootprint[hotel.footprint] + " " + hotel.number,
                value: index,
                disabled: false,
              }))}
            />
          </div>
          {/* slots */}
          <div className="auto-calibration-result-select">
            <label>Slot</label>
            <CommonDropDown<number>
              value={selectedSlotIndex}
              id="slot-select"
              disabled={useUpdatedValue}
              hideDropIcon={useUpdatedValue}
              onChange={(value) => {
                setSelectedSlotIndex(value);
              }}
              style={{
                width: 68,
              }}
              dropDownStyle={{
                maxHeight: 110,
              }}
              dropDownDisplayValues={Array(hotelCapacity)
                .fill(0)
                .map((_, index) => ({
                  value: index,
                  display: (index + 1).toString(),
                  disabled: false,
                }))}
            />
          </div>
        </div>
        <div>
          <MtiButton
            variant="secondary"
            colorVariant="alternate"
            disabled={
              moveButtonsState.get(EMoveButtonType.Store) ===
                EMoveButtonState.Disabled ||
              moveButtonsState.get(EMoveButtonType.Store) ===
                EMoveButtonState.Loading
            }
            onClick={() => {
              ToolsApi.storePlate(
                ScModuleIdentifier,
                hotels[selectedHotelIndex].number,
                selectedSlotIndex + 1,
                useUpdatedValue
                  ? EAutoCalibrationResult.UpdatedAutoCalibration
                  : EAutoCalibrationResult.CurrentAutoCalibration
              ).then(() => {
                SetMoveButtonLoadingActive(
                  EMoveButtonType.Store,
                  useUpdatedValue
                );
              });
            }}
            className={
              moveButtonsState.get(EMoveButtonType.Store) ===
              EMoveButtonState.Loading
                ? "btn-alternate-loading-animate"
                : ""
            }
            style={{
              minHeight: "37px",
              width: 90,
            }}
          >
            {moveButtonsState.get(EMoveButtonType.Store) ===
              EMoveButtonState.Loading && loadingIcon}
            {moveButtonsState.get(EMoveButtonType.Store) !==
              EMoveButtonState.Loading && "STORE"}
          </MtiButton>
          <MtiButton
            variant="secondary"
            colorVariant="alternate"
            disabled={
              moveButtonsState.get(EMoveButtonType.Retrieve) ===
                EMoveButtonState.Disabled ||
              moveButtonsState.get(EMoveButtonType.Retrieve) ===
                EMoveButtonState.Loading
            }
            onClick={() => {
              ToolsApi.retrievePlate(
                ScModuleIdentifier,
                hotels[selectedHotelIndex].number,
                selectedSlotIndex + 1,
                useUpdatedValue
                  ? EAutoCalibrationResult.UpdatedAutoCalibration
                  : EAutoCalibrationResult.CurrentAutoCalibration
              ).then(() => {
                SetMoveButtonLoadingActive(
                  EMoveButtonType.Retrieve,
                  useUpdatedValue
                );
              });
            }}
            className={
              moveButtonsState.get(EMoveButtonType.Retrieve) ===
              EMoveButtonState.Loading
                ? "btn-alternate-loading-animate"
                : ""
            }
            style={{
              minHeight: "37px",
              width: 116,
            }}
          >
            {moveButtonsState.get(EMoveButtonType.Retrieve) ===
              EMoveButtonState.Loading && loadingIcon}
            {moveButtonsState.get(EMoveButtonType.Retrieve) !==
              EMoveButtonState.Loading && "RETRIEVE"}
          </MtiButton>
        </div>
      </div>
    </div>
  );
}
